import React, { useEffect, useContext } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Spinner, SplashScreen } from '@piwikpro/ui-components';
import { AccessContext } from '@piwikpro/platform';
import { AppPermissionPass } from '@piwikpro/apps-crate/firewall/AppPermissionPass';
import { useParams } from '@tanstack/react-router';
import { checkMetaSitePermissions as checkMetaSitePermissionsPure } from '../reducers/permissions';
import { MetaSitePermissionPass } from '../firewall/MetaSitePermissionPass';
import { MetaSitesState } from '../reducers';
import { MetaSitePermissions } from '../interfaces';

interface SecuredMetaSitesOwnProps {
  component: React.ComponentType<any>
}

const mapStateToProps = (state: MetaSitesState) => ({
  levels: state.metaSites.permissions.levels,
  isLoading: state.metaSites.permissions.isLoading,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  checkMetaSitePermissions: bindActionCreators(checkMetaSitePermissionsPure, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

type SecuredMetaSitesProps = SecuredMetaSitesOwnProps & ReduxProps;

const SecuredMetaSites: React.FC<SecuredMetaSitesProps> = ({
  checkMetaSitePermissions,
  component: Component,
  levels,
  isLoading,
  ...rest
}) => {
  const params = useParams({ strict: false });
  const { accessManager, userRole } = useContext(AccessContext);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!params.metaSiteId || !userRole) {
        return;
      }

      try {
        await checkMetaSitePermissions(params.metaSiteId, userRole);
      // eslint-disable-next-line no-empty
      } catch (err) {}
    };

    fetchPermissions();
  }, [params.metaSiteId, userRole, checkMetaSitePermissions]);

  if (isLoading) {
    return (
      <Spinner />
    );
  }

  if (!(levels as MetaSitePermissions[]).includes('manage')) {
    return (
      <SplashScreen type="noPrivileges" />
    );
  }

  return (
    <AccessContext.Provider
      value={{
        // @ts-ignore:next-line
        accessManager: accessManager.create(
          new MetaSitePermissionPass(levels) as unknown as AppPermissionPass,
        ),
        userPermission: levels[levels.length - 1],
      }}
    >
      <Component {...rest} />
    </AccessContext.Provider>
  );
};

export default connector(SecuredMetaSites) as React.ComponentType<SecuredMetaSitesOwnProps>;
